import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input } from '@arco-design/web-react';

import { PluginModal } from '../../components/PluginModal';
import { type ExistingVolumeOptionItem, ExistingVolumeSelectField } from '../../components/existingVolumeSelect';
import { OptionMetaRootField, buildOptionFromRaw, buildOptionMetaPatch } from '../../platform/option-meta';
import { getModalPopupContainer } from '../../utils/modalPopupContainer';
import styles from './index.module.less';
import type { StorageDataDiskSelectModalProps } from './types';
import { buildDataDiskFromExistingSelection } from './utils';

enum Field {
  SELECTED_DISK = 'selectedDisk',
  DISK = 'disk',
}

/** 选择已有数据云盘弹窗 */
export function StorageDataDiskSelectModal({ visible, form, title, namespace, onOk, onCancel }: StorageDataDiskSelectModalProps) {
  const { t } = useTranslation();
  const openInitializedRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      openInitializedRef.current = false;
      return;
    }
    if (openInitializedRef.current) return;
    openInitializedRef.current = true;
    form.resetFields();
  }, [form, visible]);

  const handleVolumeSelect = useCallback(
    (selected: ExistingVolumeOptionItem | undefined) => {
      if (!selected) {
        form.setFieldValue(Field.DISK, undefined);
        return;
      }
      const disk = buildDataDiskFromExistingSelection(selected);
      form.setFieldValue(Field.DISK, disk);
      const formValues = form.getFieldsValue() as Record<string, unknown>;
      form.setFieldsValue({
        ...buildOptionMetaPatch(
          disk.storageClass,
          buildOptionFromRaw(disk.storageClass, { label: selected.storageClass, name: selected.storageClass }),
          formValues,
        ),
        ...buildOptionMetaPatch(
          disk.reclaimPolicy,
          buildOptionFromRaw(disk.reclaimPolicy, { label: disk.reclaimPolicy, name: disk.reclaimPolicy }),
          formValues,
        ),
      });
    },
    [form],
  );

  const handleOk = async () => {
    try {
      await form.validate();
    } catch {
      return Promise.reject();
    }
    await onOk();
  };

  const modalTitle = title ?? t('components:add.existing.data.disk');

  return (
    <PluginModal className={styles.selectModal} visible={visible} title={modalTitle} focusLock={false} onCancel={onCancel} onOk={handleOk}>
      <div className={styles.modalBody}>
        <Form form={form} layout="vertical">
          <ExistingVolumeSelectField
            form={form}
            field={Field.SELECTED_DISK}
            volumeType="Data"
            namespace={namespace}
            enabled={visible}
            getPopupContainer={getModalPopupContainer}
            onVolumeSelect={handleVolumeSelect}
          />
          <Form.Item field={Field.DISK} hidden>
            <Input />
          </Form.Item>
          <OptionMetaRootField />
        </Form>
      </div>
    </PluginModal>
  );
}

export function getStorageDataDiskSelectModalValues(form: StorageDataDiskSelectModalProps['form']) {
  return form.getFieldValue(Field.DISK) as ReturnType<typeof buildDataDiskFromExistingSelection> | undefined;
}
